import React, { useState, useRef, useEffect, useCallback } from 'react';

// A user picker of SEVERAL people (Jira's "User Picker (multiple users)",
// e.g. Project Sponsors). UserEditor's search list, but a click ticks or
// unticks a person instead of replacing the value; the people already chosen
// are listed first (they may not be among the search results). It stays open
// until a click outside it — onRequestClose — like MultiSelectEditor.
function MultiUserEditor({ value, onChange, disabled, onSearchUsers, onRequestClose }) {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  const chosen = (Array.isArray(value) ? value : []).filter(u => u && u.accountId);
  const chosenIds = new Set(chosen.map(u => String(u.accountId)));

  const fetchUsers = async (query) => {
    if (!onSearchUsers) return;
    setLoading(true);
    try {
      const result = await onSearchUsers(query);
      setUsers(result || []);
    } catch (err) {
      console.error('Failed to search users:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
    fetchUsers('');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target) && onRequestClose) onRequestClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onRequestClose]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearch(query);
    setHighlightedIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchUsers(query), 300);
  };

  // The chosen people first (matching the search), then everyone else found.
  const q = search.trim().toLowerCase();
  const matches = (u) => !q || String(u.displayName || '').toLowerCase().includes(q);
  const others = users.filter(u => u.accountType === 'atlassian' && u.active !== false && !chosenIds.has(String(u.accountId)));
  const list = [...chosen.filter(matches), ...others];

  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('.field-editor-option');
    if (items[highlightedIndex]) items[highlightedIndex].scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  const toggle = useCallback((user) => {
    if (disabled || !user) return;
    const id = String(user.accountId);
    onChange(chosenIds.has(id)
      ? chosen.filter(u => String(u.accountId) !== id)
      : [...chosen, { accountId: user.accountId, displayName: user.displayName, avatarUrls: user.avatarUrls }]);
  }, [disabled, chosen, chosenIds, onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (list.length ? (prev + 1) % list.length : -1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev <= 0 ? list.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < list.length) toggle(list[highlightedIndex]);
    } else if (e.key === 'Escape' && onRequestClose) {
      onRequestClose();
    }
  }, [list, highlightedIndex, toggle, onRequestClose]);

  return (
    <div className="field-editor-user-wrapper" ref={wrapperRef}>
      <input
        ref={searchRef}
        type="text"
        className="field-editor-search-input"
        placeholder={chosen.length ? `${chosen.length} chosen — search to add` : 'Search users...'}
        value={search}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      <div className="field-editor-options-list" ref={listRef}>
        {list.map((user, idx) => {
          const isSelected = chosenIds.has(String(user.accountId));
          return (
            <div
              key={user.accountId}
              className={`field-editor-option ${isSelected ? 'selected' : ''} ${highlightedIndex === idx ? 'highlighted' : ''}`}
              onClick={() => toggle(user)}
              onMouseEnter={() => setHighlightedIndex(idx)}
            >
              {user.avatarUrls?.['24x24'] && (
                <img src={user.avatarUrls['24x24']} alt="" className="field-editor-user-avatar" />
              )}
              <span className="field-editor-option-label">{user.displayName || user.accountId}</span>
              {isSelected && <span className="field-editor-option-check">✓</span>}
            </div>
          );
        })}
        {loading && <div className="field-editor-loading">Searching...</div>}
        {!loading && list.length === 0 && <div className="field-editor-loading">No users found</div>}
      </div>
    </div>
  );
}

export default MultiUserEditor;
